<template>
  <div class="sequence-pro-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>${{ activeFormula }}$</div>
    </div>

    <div class="split-layout">
      <!-- 渲染舞台 -->
      <div class="simulation-stage dark-stage flex-1">
        <svg width="100%" height="360" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
           <defs>
              <filter id="seq-glow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
           </defs>

           <g stroke="rgba(241, 245, 249, 0.03)" stroke-width="1.5">
              <line v-for="i in 17" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="600" />
              <line v-for="i in 13" :key="'h'+i" x1="0" :y1="i*50" x2="800" :y2="i*50" />
           </g>
           
           <!-- 坐标轴 -->
           <line x1="80" y1="500" x2="750" y2="500" stroke="#475569" stroke-width="3" />
           <line x1="80" y1="500" x2="80" y2="50" stroke="#475569" stroke-width="3" />
           <text x="730" y="540" fill="#94a3b8" font-size="20" font-weight="900">项数 (n)</text>
           <text x="50" y="60" fill="#94a3b8" font-size="20" font-weight="900">{{ mode === 'sum' ? '前n项和 (Sₙ)' : '项值 (aₙ)' }}</text>

           <!-- 极限趋近线 -->
           <g v-if="mode === 'limit' || mode === 'recurrence'">
              <line v-if="limitL !== 0" x1="80" :y1="calcY(limitL)" x2="750" :y2="calcY(limitL)" stroke="#f43f5e" stroke-width="2" stroke-dasharray="6,4" />
              <text v-if="limitL !== 0" x="650" :y="calcY(limitL) - 10" fill="#fb7185" font-size="16" font-weight="bold">渐近线 L = {{ limitL.toFixed(1) }}</text>
           </g>

           <!-- 数列元素 -->
           <g v-for="(val, index) in sequence" :key="'g'+index">
              <!-- 求和模式：直方图 -->
              <rect v-if="mode === 'sum'" :x="100 + index * 32 - 10" :y="calcRectY(val)" width="20" :height="calcRectH(val)" fill="rgba(56, 189, 248, 0.2)" stroke="#0284c7" stroke-width="1" />
              <!-- 基础项值：散点提示立柱 -->
              <rect v-else :x="100 + index * 32 - 4" :y="val >= 0 ? calcY(val) : 500" width="8" :height="Math.abs(500 - calcY(val))" fill="rgba(148, 163, 184, 0.15)" rx="2"/>
              <circle :cx="100 + index * 32" :cy="calcY(val)" r="7" :fill="dotColor" stroke="#fff" stroke-width="2" filter="url(#seq-glow)" />
              <text :x="100 + index * 32" :y="525" fill="#94a3b8" font-size="14" text-anchor="middle" font-weight="bold">{{ index + 1 }}</text>
           </g>

           <!-- 趋势连线 -->
           <path v-if="sequence.length > 1" :d="trendPath" fill="none" :stroke="dotColor" stroke-width="3" stroke-dasharray="8,5" opacity="0.6" />
        </svg>
      </div>

      <!-- 右侧解析面板 -->
      <div class="simulation-stage logic-panel">
         <div class="logic-header">逻辑解析视图</div>
         <div class="flex-column-center" style="flex:1; padding: 15px;">
            <div class="eq-box text-center">
               <div class="eq-display text-blue">当前计算项值</div>
               <div class="seq-data-list">
                  <div class="data-row"><span>项数 (n)</span><span class="hl">{{ n }}</span></div>
                  <div class="data-row"><span>第n项 (aₙ)</span><span class="hl">{{ lastTerm.toFixed(2) }}</span></div>
                  <div class="data-row" v-if="mode === 'sum'"><span>前n项和 (Sₙ)</span><span class="hl text-green">{{ sumTotal.toFixed(2) }}</span></div>
                  <div class="data-row" v-if="mode === 'periodic'"><span>周期 T</span><span class="hl text-yellow">{{ periodT }}</span></div>
               </div>
            </div>

            <div class="eq-box mt-l">
               <p class="desc-text" style="font-size: 13px;"><strong>核心奥义：</strong><br/><br/>{{ tipDesc }}</p>
            </div>
         </div>
      </div>
    </div>

    <!-- 控制台 -->
    <div class="compact-controls">
      <div class="control-card">
        <div class="control-grid-mini">
           <div class="s-item">
              <label>切换研究场景</label>
              <el-select v-model="mode" size="small" placeholder="请选择">
                 <el-option label="等差数列 (线性律)" value="arithmetic"></el-option>
                 <el-option label="等比数列 (指数律)" value="geometric"></el-option>
                 <el-option label="高阶等差 (差分法)" value="highOrder"></el-option>
                 <el-option label="递推数列 (递归模型)" value="recurrence"></el-option>
                 <el-option label="周期数列 (循环律)" value="periodic"></el-option>
                 <el-option label="斐波那契数列 (自然律)" value="fibonacci"></el-option>
                 <el-option label="前n项和与极限" value="limit"></el-option>
              </el-select>
           </div>
           <div class="s-item">
              <label>演进项数 n (当前: {{ n }})</label>
              <el-slider v-model="n" :min="5" :max="20" size="small"></el-slider>
           </div>
           
           <!-- 动态控制项 -->
           <div class="s-item" v-if="mode === 'arithmetic' || mode === 'recurrence'">
              <label>{{ mode === 'recurrence' ? '递推常数 q' : '公差 d' }}</label>
              <el-slider v-model="d" :min="-10" :max="10" size="small"></el-slider>
           </div>
           <div class="s-item" v-if="mode === 'geometric' || mode === 'recurrence'">
              <label>{{ mode === 'recurrence' ? '迭代系数 p' : '公比 q' }}</label>
              <el-slider v-model="q" :min="-2" :max="2" :step="0.1" size="small"></el-slider>
           </div>
           <div class="s-item" v-if="mode === 'periodic'">
              <label>循环周期 T</label>
              <el-slider v-model="periodT" :min="2" :max="6" size="small"></el-slider>
           </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NumberSequenceViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'arithmetic',
      n: 15,
      a1: 10,
      d: 5,
      q: 1.2,
      periodT: 3,
      alpha: -0.8
    }
  },
  mounted() {
    this.detectMode();
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    }
  },
  computed: {
    topicIntro() {
       const maps = {
          arithmetic: '等差数列：差值恒定的线性模型',
          geometric: '等比数列：按比例增长的指数模型',
          highOrder: '高阶等差：差分的差分（多项式阶跃模型）',
          recurrence: '递推数列：基于 aₙ₊₁ = p·aₙ + q 的递归动力学',
          periodic: '周期数列：呈现循环规律的离散脉冲',
          fibonacci: '斐波那契数列：黄金比例的自然递归',
          limit: '数列极限：探究无限推演中的稳定收敛性'
       };
       return maps[this.mode] || '数列图象与性质';
    },
    activeFormula() {
       if (this.mode === 'arithmetic') return 'aₙ = a₁ + (n-1)d';
       if (this.mode === 'geometric') return 'aₙ = a₁·qⁿ⁻¹';
       if (this.mode === 'highOrder') return 'aₙ = An² + Bn + C';
       if (this.mode === 'recurrence') return 'aₙ₊₁ = p·aₙ + q';
       if (this.mode === 'fibonacci') return 'aₙ = aₙ₋₁ + aₙ₋₂';
       if (this.mode === 'periodic') return 'aₙ = aₙ₊ₜ';
       return 'lim(n→∞) aₙ = L';
    },
    sequence() {
       let seq = [];
       if (this.mode === 'arithmetic') {
          for(let i=0; i<this.n; i++) seq.push(this.a1 + i * this.d);
       } else if (this.mode === 'geometric') {
          for(let i=0; i<this.n; i++) seq.push(this.a1 * Math.pow(this.q, i));
       } else if (this.mode === 'highOrder') {
          // quadratic: a_n = n^2 + n (k=2)
          for(let i=1; i<=this.n; i++) seq.push(i*i + i);
       } else if (this.mode === 'recurrence') {
          let current = this.a1;
          for(let i=0; i<this.n; i++) {
             seq.push(current);
             current = this.q * current + this.d; // p=q, q=d
          }
       } else if (this.mode === 'fibonacci') {
          let a = 1, b = 1;
          for(let i=0; i<this.n; i++) {
             seq.push(a);
             let temp = a + b;
             a = b;
             b = temp;
             if (seq.length >= this.n) break;
          }
       } else if (this.mode === 'periodic') {
          const base = [10, 30, 20, 40, 15, 35];
          for(let i=0; i<this.n; i++) {
             seq.push(base[i % this.periodT]);
          }
       } else {
          // Limit mode
          const limit = 25;
          for(let i=1; i<=this.n; i++) {
             seq.push(limit + 50 * Math.pow(-0.7, i) / (i*0.2 + 1));
          }
       }
       return seq;
    },
    limitL() {
       if (this.mode === 'recurrence') {
          // Fixed point a = p*a + q => a = q / (1-p)
          if (Math.abs(1 - this.q) > 0.1 && Math.abs(this.q) < 1) return this.d / (1 - this.q);
       }
       if (this.mode === 'limit') return 25;
       return 0;
    },
    lastTerm() {
       return this.sequence[this.sequence.length - 1] || 0;
    },
    sumTotal() {
       return this.sequence.reduce((a, b) => a + b, 0);
    },
    maxVal() {
       const vals = this.sequence.map(Math.abs);
       if (this.limitL !== 0) vals.push(Math.abs(this.limitL));
       const max = Math.max(...vals, 50);
       return max * 1.1;
    },
    trendPath() {
       let pts = this.sequence.map((v, i) => `${100 + i * 32},${this.calcY(v)}`);
       return 'M ' + pts.join(' L ');
    },
    dotColor() {
       const maps = { arithmetic: '#38bdf8', geometric: '#f43f5e', highOrder: '#fb7185', recurrence: '#fbbf24', fibonacci: '#a855f7', periodic: '#10b981', limit: '#34d399' };
       return maps[this.mode];
    },
    tipDesc() {
       if (this.mode === 'arithmetic') return '等差数列是均匀变化的。公差 d 对应一次函数的斜率。';
       if (this.mode === 'geometric') return '等比数列是爆炸性增长或快速衰减的。公比 q 决定了它是膨胀(q>1)还是震荡(q<0)。';
       if (this.mode === 'highOrder') return '二阶等差数列的一次差分是等差数列，二次差分为常数。它是从线性步进向非线性律过渡的桥梁。';
       if (this.mode === 'recurrence') return '递推数列展示了自反馈过程。当迭代系数 |p|<1 时，序列会逐渐向不动点 q/(1-p) 靠拢，这是混沌理论与现代动力学的基础。';
       if (this.mode === 'fibonacci') return '每一个数都是前两项之和。它的相邻项之比随着计算深度的增加，会无限逼近黄金分割比 0.618。';
       if (this.mode === 'periodic') return '周期性是自然界最普遍的动态之一。在离散数列中，周期性的核心是取模运算（n mod T）。';
       return '数列极限揭示了无穷过程的稳定性。无论初始如何波动，终将归于平静的界限。';
    }
  },
  methods: {
    detectMode() {
       const kp = (this.knowledgePoint || '').toString();
       if (kp.includes('高阶')) this.mode = 'highOrder';
       else if (kp.includes('递推')) this.mode = 'recurrence';
       else if (kp.includes('周期')) this.mode = 'periodic';
       else if (kp.includes('斐波') || kp.includes('黄金')) this.mode = 'fibonacci';
       else if (kp.includes('等比')) this.mode = 'geometric';
       else if (kp.includes('等差')) this.mode = 'arithmetic';
       else if (kp.includes('极限') || kp.includes('单调')) this.mode = 'limit';
    },
    calcY(v) {
       return 500 - (v / this.maxVal) * 420;
    },
    calcRectY(v) {
       return v >= 0 ? this.calcY(v) : 500;
    },
    calcRectH(v) {
       return Math.abs(500 - this.calcY(v));
    }
  }
}
</script>

<style scoped lang="scss">
.sequence-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 15px; color: #1e293b; font-weight: 700; } .formula-chip { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; padding: 8px 18px; border-radius: 6px; font-weight: 900; font-family: -apple-system, sans-serif; font-size: 18px; } }
.split-layout { display: flex; gap: 12px; flex: 1; min-height: 360px; }
.flex-1 { flex: 1; min-width: 0; }
.logic-panel { flex: 0.7; min-width: 0; display: flex; flex-direction: column; background: #0f172a !important; border-radius: 12px; }
.logic-header { background: #1e293b; padding: 12px; color: #38bdf8; font-size: 14px; font-weight: 900; text-align: center; border-bottom: 2px solid #0f172a; border-radius: 12px 12px 0 0; }
.flex-column-center { display: flex; flex-direction: column; justify-content: flex-start; }

.eq-box { width: 100%; border-radius: 8px; overflow: hidden; }
.eq-display { background: rgba(0,0,0,0.4); padding: 10px; font-weight: 900; border-bottom: 1px solid rgba(255,255,255,0.05); }
.seq-data-list { display: flex; flex-direction: column; background: rgba(15, 23, 42, 0.6); }
.data-row { display: flex; justify-content: space-between; padding: 12px 18px; border-bottom: 1px solid rgba(255,255,255,0.02); color: #94a3b8; font-size: 13px; font-weight: bold; }
.hl { color: #f8fafc; font-weight: 900; font-family: monospace; font-size: 16px; }

.text-green { color: #34d399; } .text-blue { color: #38bdf8; } .text-yellow { color: #fbbf24; }
.desc-text { color: #cbd5e1; font-size: 13px; line-height: 1.6; padding: 15px; background: rgba(56, 189, 248, 0.05); border-radius: 8px; border: 1px solid rgba(56, 189, 248, 0.1); }
.mt-l { margin-top: 25px; }

.simulation-stage { background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }

.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: #ffffff; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; .s-item { label { font-size: 13px; color: #475569; font-weight: 900; margin-bottom: 8px; display: block; } } }
</style>
